import { useFile } from "#src/hooks/useFile";
import {
  Button,
  FileUploadInput,
  Form,
  TextAreaInput,
  Tooltip,
  useForm,
} from "@validereinc/common-components";
import {
  type ArtifactRefType,
  MAX_INVESTIGATION_MESSAGE_LENGTH,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import styles from "./InvestigationComposer.module.scss";

const cx = classNames.bind(styles);

const TEXTAREA_MAX_HEIGHT_PX = 200;

type InvestigationComposerFormType = {
  content: string;
  attachments: Array<{
    ref: string;
    name: string;
    mime_type?: string;
  }>;
};

export type InvestigationComposerSubmitPayloadType = {
  content: string;
  metadata?: {
    artifacts: ArtifactRefType[];
  };
};

export type InvestigationComposerProps = {
  placeholder: string;
  textareaLabel: string;
  maxLengthErrorMessage: string;
  attachmentsInputId: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmit: (payload: InvestigationComposerSubmitPayloadType) => Promise<void>;
};

export const InvestigationComposer = ({
  placeholder,
  textareaLabel,
  maxLengthErrorMessage,
  attachmentsInputId,
  isDisabled = false,
  isLoading = false,
  onSubmit,
}: InvestigationComposerProps) => {
  const form = useForm<InvestigationComposerFormType>({
    mode: "onChange",
    defaultValues: { content: "", attachments: [] },
  });
  const [fileUploadSessionKey, setFileUploadSessionKey] = useState(0);
  const chatInputRef = useRef<HTMLDivElement>(null);

  const { handleFileUpload, handleFileDownload } = useFile();
  const [attachmentUploadInFlightCount, setAttachmentUploadInFlightCount] =
    useState(0);
  const isAttachmentProcessing = attachmentUploadInFlightCount > 0;

  const promptContent = form.watch("content");
  const attachments = form.watch("attachments") ?? [];
  const hasSubmittableContent = Boolean(
    promptContent.trim() || attachments.length
  );

  const isSendDisabled =
    isDisabled ||
    isLoading ||
    isAttachmentProcessing ||
    !hasSubmittableContent ||
    !form.formState.isValid;

  const uploadAttachment = async (
    ...args: Parameters<typeof handleFileUpload>
  ) => {
    const [file] = args;
    setAttachmentUploadInFlightCount((count) => count + 1);
    try {
      const uploadedFile = await handleFileUpload(...args);
      if (!uploadedFile || !file) return uploadedFile;

      return {
        ...uploadedFile,
        mime_type: file.type || "application/octet-stream",
      };
    } finally {
      setAttachmentUploadInFlightCount((count) => Math.max(0, count - 1));
    }
  };

  const handleSubmit = form.handleSubmit(async ({ content }) => {
    if (!hasSubmittableContent) return;

    const artifacts: ArtifactRefType[] = attachments.map((attachment) => ({
      kind: "dp_file",
      name: attachment.name,
      mime_type: attachment.mime_type ?? "application/octet-stream",
      ref: {
        file_id: attachment.ref,
      },
    }));

    try {
      await onSubmit({
        content: content.trim() || " ",
        metadata: artifacts.length ? { artifacts } : undefined,
      });
    } catch {
      return;
    }

    form.reset({ content: "", attachments: [] });
    setFileUploadSessionKey((sessionKey) => sessionKey + 1);
  });

  const submitPrompt = useCallback(() => {
    void handleSubmit();
  }, [handleSubmit]);

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
  }, []);

  useEffect(() => {
    const textarea =
      chatInputRef.current?.querySelector<HTMLTextAreaElement>("textarea");
    if (!textarea) return;

    if (!promptContent) {
      textarea.style.height = "auto";
      return;
    }

    resizeTextarea(textarea);
  }, [promptContent, resizeTextarea]);

  const handleTextareaInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    resizeTextarea(event.currentTarget);
  };

  const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    ) {
      return;
    }

    event.preventDefault();

    if (isSendDisabled) return;

    submitPrompt();
  };

  const openAttachmentPicker = () => {
    document.getElementById(attachmentsInputId)?.click();
  };

  return (
    <Form {...form}>
      <div
        ref={chatInputRef}
        className={cx("chat-input", {
          "chat-input--disabled": isDisabled,
        })}
        data-testid="investigation-composer"
      >
        <TextAreaInput
          name="content"
          label={textareaLabel}
          isLabelShown={false}
          placeholder={placeholder}
          isFluid
          isDisabled={isDisabled || isLoading}
          rows={1}
          className={cx("chat-input__textarea-field")}
          onInput={handleTextareaInput}
          onKeyDown={handleTextareaKeyDown}
          rules={{
            validate: {
              maxLength: (value: string) =>
                value.length <= MAX_INVESTIGATION_MESSAGE_LENGTH ||
                maxLengthErrorMessage,
            },
          }}
        />

        <div className={cx("chat-input__file-upload")}>
          <FileUploadInput
            key={fileUploadSessionKey}
            name="attachments"
            id={attachmentsInputId}
            label="Attachments"
            isLabelShown={false}
            isRequired={false}
            allowMultiple
            className={cx("chat-input__hidden-file-picker")}
            uploadFile={uploadAttachment}
            downloadFile={handleFileDownload}
          />
        </div>

        <div className={cx("chat-input__actions")}>
          <Tooltip
            content="Add attachment"
            contentClassName={cx("chat-input__tooltip-trigger")}
          >
            <Button
              type="button"
              variant="outline"
              size="xx-small"
              icon="paperclip"
              aria-label="Add attachment"
              disabled={isDisabled || isLoading}
              onClick={openAttachmentPicker}
            />
          </Tooltip>

          <Tooltip
            content="Send prompt"
            contentClassName={cx("chat-input__tooltip-trigger")}
          >
            <Button
              type="button"
              variant="primary"
              size="small"
              icon="paper-plane-tilt"
              className={cx("chat-input__send-button")}
              aria-label="Send prompt"
              isLoading={isLoading}
              disabled={isSendDisabled}
              onClick={submitPrompt}
            />
          </Tooltip>
        </div>
      </div>
    </Form>
  );
};
